
import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";
import MaterialTable from "material-table";
import axios from "axios";
import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";
import { API } from "../../../backend";
import authHeader from "../../../services/auth-header";
import { getTrans as t } from "../../../Localization/translations";
import Helper from "../../../Helper";
import GetProcessingService from "../../../services/Processing/processview";

function AllProcessingFailureLog() {

    const tableRef = React.createRef();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const location = useLocation();
    const navigate = useNavigate();
    const filterRequestId = location.state?.request_id ?? null;

    // Process view pane (opened by clicking the Request ID hyperlink)
    const [viewPane, setViewPane] = useState({ isPaneOpen: false });
    const [request_id, setRequestID] = useState("");
    const [process_name, setProcessName] = useState("");
    const [parameter1, setParameter1] = useState("");
    const [parameter2, setParameter2] = useState("");
    const [status, setStatus] = useState("");
    const [error_details, setErrorDetails] = useState("");
    const [next_run_date, setNextRunDate] = useState("");
    const [detailsExpanded, setDetailsExpanded] = useState(false);

    // Edit-schedule pane
    const [editPane, setEditPane] = useState({ isPaneOpen: false });
    const [editProcessId, setEditProcessId] = useState(null);
    const [editDate, setEditDate] = useState('');
    const [editTime, setEditTime] = useState('');
    const [editFrequency, setEditFrequency] = useState('');
    const [editIsSpecific, setEditIsSpecific] = useState(false);
    const [editSaving, setEditSaving] = useState(false);

    const formatDate = (date) => {
        const newDate = new Date(date);
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, '0');
        const day = String(newDate.getDate()).padStart(2, '0');
        const hours = String(newDate.getHours()).padStart(2, '0');
        const minutes = String(newDate.getMinutes()).padStart(2, '0');
        const seconds = String(newDate.getSeconds()).padStart(2, '0');
        return `${Helper.DateNormal(`${year}-${month}-${day}`)}  ${hours}:${minutes}:${seconds}`;
    };

    const openProcessView = (id) => {
        GetProcessingService.getProcessing(id).then((response) => {
            const proc = response.data.response;
            setRequestID(proc.id);
            setProcessName(proc.process_name);
            setParameter1(proc.parameter1);
            setParameter2(proc.parameter2);
            setStatus(proc.status);
            setErrorDetails(proc.process_log);
            setNextRunDate(proc.next_run_date);
            setDetailsExpanded(false);
            setViewPane({ isPaneOpen: true });
        });
    };

    const openEditSchedule = () => {
        const current = new Date(next_run_date);
        const pad = (n) => String(n).padStart(2, '0');
        setEditProcessId(request_id);
        setEditDate(`${current.getFullYear()}-${pad(current.getMonth() + 1)}-${pad(current.getDate())}`);
        setEditTime(`${pad(current.getHours())}:${pad(current.getMinutes())}`);
        setEditFrequency('');
        setEditIsSpecific(false);
        setViewPane({ isPaneOpen: false });
        setEditPane({ isPaneOpen: true });

        GetProcessingService.getProcessing(request_id).then((res) => {
            const intervalType = res.data && res.data.response ? res.data.response.interval_type : null;
            if (intervalType === 'day' || intervalType === 'week' || intervalType === 'month') {
                setEditFrequency(intervalType);
            } else if (intervalType === 'specific') {
                setEditIsSpecific(true);
            }
        }).catch(() => { /* non-fatal — dropdown just stays unselected */ });
    };

    const handleSaveReschedule = () => {
        if (!editDate || !editTime) {
            return;
        }
        setEditSaving(true);
        const localDateTime = new Date(`${editDate}T${editTime}`);
        const utcString = localDateTime.toISOString();
        GetProcessingService.rescheduleProcessing(editProcessId, utcString, editIsSpecific ? '' : editFrequency).then(() => {
            setEditSaving(false);
            setEditPane({ isPaneOpen: false });
        }).catch(() => {
            setEditSaving(false);
        });
    };

    const parsedDetails = useMemo(() => {
        if (!error_details) return null;
        try {
            const parsed = JSON.parse(error_details);
            return (parsed && typeof parsed === "object") ? parsed : null;
        } catch (e) {
            return null;
        }
    }, [error_details]);

    const reportUrl = parsedDetails && typeof parsedDetails.report_excel_url === "string"
        ? parsedDetails.report_excel_url
        : null;

    const DETAILS_PREVIEW_LENGTH = 300;
    const detailsText = useMemo(() => {
        if (!error_details) return "";
        if (parsedDetails) {
            const rest = _.omit(parsedDetails, ["report_excel_url"]);
            return JSON.stringify(rest, null, 2);
        }
        return error_details;
    }, [error_details, parsedDetails]);

    // Older rows persisted before these messages were reworded still carry
    // the original text — remap at display time so the whole log reads
    // consistently regardless of when the row was created.
    const getDisplayMessage = (message) => {
        if (!message) return message;
        if (message.startsWith("Contract has no ach_account_id configured")) {
            return message.replace(
                "Contract has no ach_account_id configured",
                "Contract is not set up for ACH"
            );
        }
        if (message.startsWith("An unexpected error occurred while processing this record")) {
            return message.replace(
                "An unexpected error occurred while processing this record.",
                "Log a support ticket."
            );
        }
        return message;
    };

    // Maps each known backend error message to the concrete step that
    // resolves it, so the log tells the reader what to do, not just what
    // went wrong.
    const getResolution = (rowData) => {
        const message = getDisplayMessage(rowData.error_message) || "";

        if (rowData.error_type === "Skipped") {
            if (message.startsWith("Contract is not set up for ACH")) {
                return "Add an ACH bank account to the contract, then re-run this batch.";
            }
            return "No action needed — this record was intentionally skipped.";
        }
        if (message.startsWith("Log a support ticket.")) {
            return "A support ticket has been logged automatically — open it from the ticket icon on the Processing page.";
        }
        if (message.includes("linked to this record could not be found")) {
            return "Check whether the linked record was deleted or the reference is invalid, then re-run this request.";
        }
        if (message.startsWith("More than one matching record was found")) {
            return "Duplicate records exist for this item — de-duplicate them, then re-run this request.";
        }
        if (message.startsWith("A database rule was violated")) {
            return "Check for a duplicate or invalid value on this record, then re-run this request.";
        }
        if (message.startsWith("The record's data did not pass validation")) {
            return "Review and correct this record's data, then re-run this request.";
        }
        if (message.startsWith("A required piece of information was missing")) {
            return "Fill in the missing field on this record, then re-run this request.";
        }
        if (message.startsWith("An expected field or related record was missing")) {
            return "Check that all related records exist for this item, then re-run this request.";
        }
        if (message.startsWith("The data for this record was not in the expected format")) {
            return "Correct this record's data format, then re-run this request.";
        }
        if (message.startsWith("The data for this record contained an invalid value")) {
            return "Correct the invalid value on this record, then re-run this request.";
        }
        return "-";
    };

    useEffect(() => {
        loadProcessingFailures();
    }, [filterRequestId]);

    const loadProcessingFailures = async () => {
        try {

            setIsLoading(true);

            const res = await axios.get(API + "processing/processing-failure-logs/", {
                headers: authHeader(),
            });

            let rowData = res.data || [];
            if (!Array.isArray(rowData)) rowData = [];

            if (filterRequestId) {
                rowData = rowData.filter(
                    (row) => String(row.request_id) === String(filterRequestId)
                );
            }

            setData(rowData);
            setIsLoading(false);

        } catch (error) {

            console.error("Error loading processing error logs:", error);
            setIsLoading(false);

        }
    };

    return (
        <div className="page-wrapper">

            <div className="content container-fluid">

                <div className="row">

                    <div className="col-md-12">

                        <MaterialTable
                            tableRef={tableRef}
                            isLoading={isLoading}
                            style={{
                                backgroundColor: "var(--bs-table-bg)",
                                borderBottomWidth: "1px",
                                boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                            }}
                            title={
                                <h5 className="mb-0">
                                    {t("Processing Error Log")}
                                    {/* {filterRequestId && (
                                        <span className="ms-2 text-muted" style={{ fontSize: 14, fontWeight: "normal" }}>
                                            — Request #{filterRequestId}
                                            <span
                                                className="ms-2 hyperlink"
                                                style={{ fontSize: 12, cursor: "pointer" }}
                                                onClick={() => navigate("/processing-failure-log", { replace: true })}
                                            >
                                                View All
                                            </span>
                                        </span>
                                    )} */}
                                </h5>
                            }

                            columns={[

                                {
                                    title: t("Request ID"),
                                    field: "request_id",
                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (rowData) => (
                                        <div
                                            className="text-bold hyperlink"
                                            onClick={() => openProcessView(rowData.request_id)}
                                        >
                                            {rowData.request_id}
                                        </div>
                                    ),
                                },
                                {
                                    title: t("Process Name"),
                                    field: "process_name",
                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (rowData) => (
                                        rowData.process_name === "Billing" ? "Generate Invoices"
                                            : rowData.process_name === "Notification" ? "Generate Notifications"
                                            : rowData.process_name === "Bucket" ? "Generate Aging Bucket"
                                            : rowData.process_name === "Contract Bulk Create" ? "Contract Bulk Create(QuickFi)"
                                            : rowData.process_name
                                    ),
                                },
                                {
                                    title: t("Step"),
                                    field: "step_name",
                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (rowData) => rowData.step_name || "-",
                                },
                                {
                                    title: t("Contract"),
                                    field: "contract_number",
                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (rowData) => (
                                        rowData.contract_number ? (
                                            <div
                                                className="hyperlink"
                                                onClick={() => navigate("/contract/view", { state: rowData.contract_id })}
                                            >
                                                {rowData.contract_number}
                                            </div>
                                        ) : "-"
                                    ),
                                },
                                {
                                    title: t("Transaction"),
                                    field: "invoice_number",
                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (rowData) => (
                                        rowData.invoice_number ? (
                                            <div
                                                className="hyperlink"
                                                onClick={() => navigate("/billing/viewinvoices", { state: rowData.invoice_id })}
                                            >
                                                {rowData.invoice_number}
                                            </div>
                                        ) : "-"
                                    ),
                                },
                                {
                                    title: t("Type"),
                                    field: "error_type",
                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12 },
                                    render: (rowData) => (
                                        <div
                                            className={
                                                rowData.error_type === "Skipped"
                                                    ? "badge badge-sm bg-gradient-secondary"
                                                    : "badge bg-gradient-danger text-xxs"
                                            }
                                        >
                                            {rowData.error_type || "Error"}
                                        </div>
                                    ),
                                },
                                {
                                    title: t("Message"),
                                    field: "error_message",
                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12, maxWidth: 320 },
                                    render: (rowData) => (
                                        <div
                                            title={getDisplayMessage(rowData.error_message)}
                                            style={{
                                                maxWidth: 320,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {getDisplayMessage(rowData.error_message)}
                                        </div>
                                    ),
                                },
                                {
                                    title: t("Resolution"),
                                    field: "resolution",
                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                    cellStyle: { textAlign: "left", fontSize: 12, maxWidth: 320 },
                                    render: (rowData) => (
                                        <div title={getResolution(rowData)} style={{ maxWidth: 320 }}>
                                            {getResolution(rowData)}
                                        </div>
                                    ),
                                },

                            ]}
                            data={data || []}

                            options={{
                                headerStyle: {
                                    backgroundColor: "white",
                                    color: "#245C7F",
                                    fontSize: 12,
                                },
                                rowStyle: (rowData, index) => ({
                                    backgroundColor: index % 2 ? "#fff" : "inherit",
                                }),
                                search: true,
                                paging: true,
                                pageSize: 10,
                                pageSizeOptions: [10, 20, 25, 50],
                                paginationType: "stepped",
                                showFirstLastPageButtons: false,
                                emptyRowsWhenPaging: false,
                                actionsColumnIndex: -1,
                            }}
                        />

                        <SlidingPane
                            className="some-custom-class sidepanebarprocess"
                            overlayClassName="custom-overlay"
                            isOpen={viewPane.isPaneOpen}
                            title="Process View"
                            subtitle=""
                            onRequestClose={() => setViewPane({ isPaneOpen: false })}
                        >
                            <div className="col-md-12">
                                <div className="row">
                                    <div className="col-xl-6">
                                        <label className="col-lg-12 col-form-label">{t('Request ID')}</label>
                                    </div>
                                    <div className="col-xl-6">
                                        <span className="col-lg-8 view-label p">
                                            {request_id ? request_id : "None"}
                                        </span>
                                    </div>
                                    <div className="col-xl-6">
                                        <label className="col-lg-12 col-form-label">{t('Porcess Name')}</label>
                                    </div>
                                    <div className="col-xl-6">
                                        <span className="col-lg-8 view-label p">
                                            {process_name === "Billing" ? "Generate Invoices" : process_name === "Notification" ? "Generate Notifications" : process_name === "Bucket" ? "Generate Aging Bucket" : process_name === "Contract Bulk Create" ? "Contract Bulk Create(QuickFi)" : process_name}
                                        </span>
                                    </div>
                                    <div className="col-xl-6">
                                        <label className="col-lg-12 col-form-label">{t('Parameter 1')}</label>
                                    </div>
                                    <div className="col-xl-6">
                                        <span className="col-lg-8 view-label p">
                                            {parameter1 ? parameter1 : "None"}
                                        </span>
                                    </div>
                                    <div className="col-xl-6">
                                        <label className="col-lg-12 col-form-label">{t('Parameter 2')}</label>
                                    </div>
                                    <div className="col-xl-6">
                                        <span className="col-lg-8 view-label p">
                                            {(parameter2) ? Helper.DateNormal(parameter2) : "None"}
                                        </span>
                                    </div>
                                    <div className="col-xl-6">
                                        <label className="col-lg-12 col-form-label">{t('Status')}</label>
                                    </div>
                                    <div className="col-xl-6">
                                        <span className="col-lg-8 view-label p">
                                            {status ? status : "None"}
                                        </span>
                                    </div>
                                    <div className="col-xl-6">
                                        <label className="col-lg-12 col-form-label">{t('Details')}</label>
                                    </div>
                                    <div className="col-xl-6">
                                        {!error_details ? (
                                            <span className="col-lg-8 view-label p">None</span>
                                        ) : (
                                            <div className="col-lg-8" style={{ width: "100%", maxWidth: "100%" }}>
                                                {reportUrl && (
                                                    <div
                                                        className="d-flex align-items-center mb-2"
                                                        style={{ gap: 6, fontSize: 12 }}
                                                    >
                                                        <i className="fa fa-light fa-file-excel" style={{ fontSize: 12, color: "#1d6f42" }}></i>
                                                        <a
                                                            href={reportUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={reportUrl}
                                                            style={{
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                maxWidth: 180,
                                                            }}
                                                        >
                                                            {t("View Report")}
                                                        </a>
                                                        <a
                                                            href={reportUrl}
                                                            download
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={t("Download Report")}
                                                            className="text-secondary"
                                                        >
                                                            <i className="fa fa-light fa-download" style={{ fontSize: 12 }}></i>
                                                        </a>
                                                    </div>
                                                )}
                                                {detailsText && (
                                                    <>
                                                        <pre
                                                            className="view-label p"
                                                            style={{
                                                                whiteSpace: "pre-wrap",
                                                                wordBreak: "break-word",
                                                                overflowWrap: "break-word",
                                                                fontFamily: "inherit",
                                                                fontSize: 12,
                                                                lineHeight: 1.4,
                                                                margin: 0,
                                                                padding: "6px 8px",
                                                                background: "#f8f9fa",
                                                                border: "1px solid #e9ecef",
                                                                borderRadius: 4,
                                                                width: "100%",
                                                                maxWidth: "100%",
                                                                boxSizing: "border-box",
                                                                overflowX: "hidden",
                                                            }}
                                                        >
                                                            {detailsExpanded || detailsText.length <= DETAILS_PREVIEW_LENGTH
                                                                ? detailsText
                                                                : `${detailsText.slice(0, DETAILS_PREVIEW_LENGTH)}...`}
                                                        </pre>
                                                        {detailsText.length > DETAILS_PREVIEW_LENGTH && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link p-0 mt-1"
                                                                style={{ fontSize: 12 }}
                                                                onClick={() => setDetailsExpanded(!detailsExpanded)}
                                                            >
                                                                {detailsExpanded ? t("View Less") : t("View More")}
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-xl-6">
                                        <label className="col-lg-12 col-form-label">{t('Next Run Date')}</label>
                                    </div>
                                    <div className="col-xl-6">
                                        <span className="col-lg-8 view-label p">
                                            {next_run_date ? formatDate(next_run_date) : "None"}
                                        </span>
                                    </div>
                                    {status === 'pending' && next_run_date && (
                                        <div className="col-lg-12 mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-primary ms-auto mb-0"
                                                onClick={openEditSchedule}
                                            >
                                                {t("Edit Schedule")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </SlidingPane>

                        <SlidingPane
                            className="some-custom-class"
                            overlayClassName="some-custom-overlay-class"
                            isOpen={editPane.isPaneOpen}
                            title="Edit Schedule"
                            subtitle=""
                            onRequestClose={() => setEditPane({ isPaneOpen: false })}
                        >
                            <div className="col-md-12">
                                <div className="col-lg-12 mb-3">
                                    <label className="col-lg-12 col-form-label">
                                        {t("New Date")}
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={editDate}
                                        onChange={(e) => setEditDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-lg-12 mb-3">
                                    <label className="col-lg-12 col-form-label">
                                        {t("New Time")}
                                    </label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={editTime}
                                        onChange={(e) => setEditTime(e.target.value)}
                                    />
                                </div>
                                {!editIsSpecific && (
                                    <div className="col-lg-12 mb-3">
                                        <label className="col-lg-12 col-form-label">
                                            {t("Frequency ")}
                                        </label>
                                        <select
                                            value={editFrequency}
                                            onChange={(e) => setEditFrequency(e.target.value)}
                                            className="form-control"
                                        >
                                            <option value="">{t("---Select---")}</option>
                                            <option value="day">{t("Days")}</option>
                                            <option value="week">{t("Weeks")}</option>
                                            <option value="month">{t("Months")}</option>
                                        </select>
                                    </div>
                                )}
                                <div className="col-lg-12">
                                    <button
                                        type="button"
                                        className="btn bg-gradient-secondary ms-auto mb-0"
                                        onClick={() => setEditPane({ isPaneOpen: false })}
                                    >
                                        {t("Back")}
                                    </button>
                                    &nbsp;&nbsp;
                                    <button
                                        type="button"
                                        disabled={editSaving}
                                        onClick={handleSaveReschedule}
                                        className="btn btn-primary ms-auto mb-0"
                                    >
                                        {editSaving ? t("Saving...") : t("Save")}
                                    </button>
                                </div>
                            </div>
                        </SlidingPane>

                    </div>

                </div>

            </div>

        </div>
    );
}

export default AllProcessingFailureLog;
